import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const ResetPassword = () => {
    const { t } = useLanguage();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [validToken, setValidToken] = useState(false);
    const [checkingToken, setCheckingToken] = useState(true);
    
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    useEffect(() => {
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        if (!token) {
            setError(t('auth.resetPasswordFlow.errorNoToken', 'No reset token provided'));
            setCheckingToken(false);
            return;
        }

        try {
            await api.get(`/verify-reset-token.php?token=${token}`);
            setValidToken(true);
            setError('');
        } catch (err) {
            setError(t('auth.resetPasswordFlow.errorInvalidToken', 'Invalid or expired reset link'));
        } finally {
            setCheckingToken(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            setError(t('auth.resetPasswordFlow.errorMismatch', 'Passwords do not match'));
            return;
        }

        if (password.length < 6) {
            setError(t('auth.resetPasswordFlow.errorLength', 'Password must be at least 6 characters'));
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            await api.post('/reset-password.php', {
                token,
                password,
                confirmPassword
            });
            setMessage(t('auth.resetPasswordFlow.success', 'Password reset successfully!'));
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || t('auth.resetPasswordFlow.defaultError', 'An error occurred'));
        } finally {
            setLoading(false);
        }
    };

    if (checkingToken) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">{t('auth.resetPasswordFlow.verifying', 'Verifying your reset link...')}</div>
            </div>
        );
    }

    if (!validToken) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8 text-center">
                    <div>
                        <h2 className="text-3xl font-extrabold text-gray-900">{t('auth.resetPasswordFlow.invalidTitle', 'Invalid Link')}</h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {t('auth.resetPasswordFlow.invalidDesc', 'This password reset link is invalid or has expired.')}
                        </p>
                    </div>
                    <div>
                        <Link
                            to="/forgot-password"
                            className="text-blue-600 hover:text-blue-500"
                        >
                            {t('auth.resetPasswordFlow.requestNew', 'Request a new reset link')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {t('auth.resetPasswordFlow.title', 'Reset Your Password')}
                    </h2>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                {t('auth.resetPasswordFlow.newPassword', 'New Password')}
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder={t('auth.resetPasswordFlow.newPasswordPlaceholder', 'Enter new password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                                {t('auth.resetPasswordFlow.confirmPassword', 'Confirm Password')}
                            </label>
                            <input
                                id="confirm-password"
                                name="confirm-password"
                                type="password"
                                required
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder={t('auth.resetPasswordFlow.confirmPasswordPlaceholder', 'Confirm new password')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm text-center">{error}</div>
                    )}
                    
                    {message && (
                        <div className="text-green-500 text-sm text-center">{message}</div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {loading ? t('auth.resetPasswordFlow.resetting', 'Resetting...') : t('auth.resetPasswordFlow.submit', 'Reset Password')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;